import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import { printQrLabel, downloadQrLabel } from '../utils/qrLabel';

// Read-only, printable QR label for a record's code. The QR is drawn
// client-side from the code text; nothing is stored.
// `compact`: the QR alone, small, for a table row; printing stays with the full label.
export default function QRCodeLabel({ value, size = 72, caption = '', compact = false }) {
  const { t } = useTranslation('common');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!value) { setQrDataUrl(''); return; }
    let cancelled = false;
    QRCode.toDataURL(value, { width: 220, margin: 1 })
      .then(url => { if (!cancelled) setQrDataUrl(url); })
      .catch(() => { if (!cancelled) setQrDataUrl(''); });
    return () => { cancelled = true; };
  }, [value]);

  if (!value) return null;
  // What goes under the QR on paper: the code, and what it is when that is known.
  const printed = caption ? `${value} · ${caption}` : value;

  // The code as text, for a spreadsheet or a message. Clipboard access can be
  // refused (plain http on a phone); then nothing is claimed.
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  if (compact) {
    return qrDataUrl
      ? <img src={qrDataUrl} alt={value} title={value} data-testid="qr-compact"
             style={{ width: size, height: size, border: '1px solid var(--c-border)', borderRadius: 4, display: 'block' }} />
      : null;
  }

  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
      {qrDataUrl && (
        <img src={qrDataUrl} alt="QR" style={{ width: size, height: size, border: '1px solid var(--c-border)', borderRadius: 6 }} />
      )}
      <div>
        <div style={{ fontFamily: 'monospace', fontSize: 14 }} dir="ltr">{value}</div>
        {caption && <div style={{ fontSize: 13 }} data-testid="qr-caption">{caption}</div>}
        {qrDataUrl && (
          <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
            <button type="button" onClick={() => printQrLabel(qrDataUrl, printed)} style={btnStyle('var(--c-success)')}
              title={t('qrField.printTip')}>{t('qrField.print')}</button>
            <button type="button" onClick={() => downloadQrLabel(qrDataUrl, printed)} style={btnStyle('var(--c-success)')}
              title={t('qrField.downloadTip')}>{t('qrField.download')}</button>
            <button type="button" onClick={copy} style={btnStyle('var(--c-info-2)')} data-testid="qr-copy"
              title={t('qrField.copyTip')}>{copied ? t('qrField.copied') : t('qrField.copy')}</button>
          </div>
        )}
      </div>
    </div>
  );
}

const btnStyle = (bg) => ({
  background: bg, color: 'var(--c-on-accent)', border: 'none', borderRadius: 6,
  padding: '6px 12px', cursor: 'pointer', fontSize: 13,
});
